import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from "common/toast";
import { useCallback, useEffect, useState } from "react";
import {
    DragDropContext,
    Draggable,
    Droppable
} from 'react-beautiful-dnd';
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import { createSelector } from "reselect";
import { getConfigDisplayInventory as onGetConfigDisplayInventory, putStateConfigDisplayInventory as onPutStateConfigDisplayInventory, putConfigDisplayInventory } from "../../../../store/thunks";
import { ARRAY_CONFIG_DISPLAY_INVENTORY_LIST } from "helpers/constans";

const menuStyle: React.CSSProperties = {
    width: '30px',
    height: '4px',
    backgroundColor: '#878a99',
    margin: '6px 0',
    borderRadius: '3px'
};

interface Item {
    id: number;
    text: string;
    key: string;
    order?: number,
    value: number,
    disabled?: boolean
}

const reorder = (list: Item[], startIndex: number, endIndex: number): Item[] => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
};

const grid = 8;

const getItemStyle = (isDragging: boolean, draggableStyle: React.CSSProperties): React.CSSProperties => ({
    userSelect: 'none',
    padding: grid * 2,
    margin: `0 0 ${grid}px 0`,
    background: isDragging ? '#eaeaea' : '#fff',
    borderRadius: '4px',
    ...draggableStyle
});

const getListStyle = (isDraggingOver: boolean): React.CSSProperties => ({
    background: isDraggingOver ? '#F0F9FF' : '#F7F9FC',
    padding: '16px 12px',
    width: '100%',
    borderRadius: '8px',
    // boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
    margin: '0',
    height: '70dvh',
    minHeight: '400px',
    overflowY: 'auto'
});

const ConfigDisplayInventory = ({
    isOpen = false,
    onClose,
    onTriggerRefresh,
}: any) => {
    const { t, i18n } = useTranslation();

    const dispatch: any = useDispatch();

    const selectLayoutState = (state: any) => state.Management;

    const ManagementProperties = createSelector(
        selectLayoutState,
        (state) => ({
            configDisplayInventory: state.configDisplayInventory,
            isConfigDisplayInventorySuccess: state.isConfigDisplayInventorySuccess,
            isConfigDisplayInventoryLoading: state.isConfigDisplayInventoryLoading,
            error: state.error,
        })
    );

    const { configDisplayInventory } = useSelector(ManagementProperties);

    const [configs, setConfig] = useState<any>(configDisplayInventory);

    const [items, setItems] = useState<Item[]>(ARRAY_CONFIG_DISPLAY_INVENTORY_LIST);

    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        if (!!isOpen) {
            dispatch(onGetConfigDisplayInventory({ type: 'inventories' }));
        }
    }, [isOpen]);

    useEffect(() => {
        setConfig((_prev: any) => configDisplayInventory);
        setItems((_prev) => {
            return (_prev)?.map((_i) => {
                const _v = getValueWithKey(_i?.key, configDisplayInventory);
                return ({
                    ..._i,
                    value: _v?.value,
                    sort: _v?.sort,
                });
            }).sort((a, b) => a?.sort - b?.sort);
        });
    }, [JSON.stringify(configDisplayInventory)]);

    const onCloseClick = () => {
        onClose && onClose();
    };

    const getValueWithKey = useCallback((_key: string, __configs: any) => {
        const _keys = String(_key)?.split('*') || [];
        const _v = (__configs?.[`${_keys[0]}`])?.find((_c: { field: string, value: number }) => (_c?.field === `${_keys?.[1]}`));
        return _v;
    }, []);

    const changeValueWithId = useCallback((_id: number, _value: number) => {
        setItems((_prev) => {
            return (_prev)?.map((_i) => (String(_id) === String(_i?.id) ? { ..._i, value: _value } : _i));
        });
    }, []);

    const changeValueWithKey = useCallback((_key: string, _value: { sort: number, value: number }, __configs: any) => {
        const _keys = String(_key)?.split('*') || [];
        const _key_first: any = (__configs?.[`${_keys[0]}`] || [])?.map((_c: { field: string, value: number }) => ((_c?.field === `${_keys?.[1]}`) ? { ..._c, ..._value } : _c));
        const _configs_new = {
            ...__configs,
            [`${_keys[0]}`]: _key_first
        };
        return _configs_new;
    }, []);


    const handleSubmit = async (is_reset: boolean = false) => {
        try {
            setIsLoading((_prev) => true);
            const _configs_data = (is_reset ? ARRAY_CONFIG_DISPLAY_INVENTORY_LIST : items)?.reduce((__c: any, item: any, index: number) => {
                return changeValueWithKey(item?.key, { value: item?.value, sort: index + 1 }, __c);
            }, configs);

            const data = {
                type: "inventories",
                fields: _configs_data
            };
            const response: any = await putConfigDisplayInventory(data);
            if (response?.code === 200) {
                setIsLoading((_prev) => false);
                toast(
                    `${t("The process has been completed.")}`,
                    CONFIG_OPTION_TOAST_NORMAL
                );
                dispatch(onPutStateConfigDisplayInventory(response?.data?.fields || {}))
                onCloseClick();
            } else {
                setIsLoading((_prev) => false);
                toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
            }
        } catch (error: any) {
            setIsLoading((_prev) => false);
            toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
            return error;
        }
    };


    const getList = (id: string) => {
        return items;
    };

    const onDragEnd = (result: any) => {
        const { source, destination } = result;
        const NUMBER_ITEM_DISABLED = 4;
        if (!destination || destination?.index < NUMBER_ITEM_DISABLED) {
            return;
        }

        const sourceList = getList(source.droppableId);
        const destinationList = getList(destination.droppableId);

        // Get the actual source and destination indexes based on the filtered list
        const sourceIndex = sourceList.findIndex(item => item.id === parseInt(result.draggableId, 10));
        const destinationIndex = destination.index;

        if (source.droppableId === destination.droppableId) {
            const reorderedItems = reorder(
                sourceList,
                sourceIndex,
                destinationIndex
            );
            setItems(reorderedItems);
        }
    };


    return (
        <>
            <Modal
                isOpen={isOpen}
                centered={true}
                size="md"
                scrollable={false}
                toggle={onCloseClick}
                keyboard={true}
            >
                <ModalHeader toggle={onCloseClick}>
                    <>{t("Config Display")}</>
                </ModalHeader>
                <ModalBody className="pt-3 pb-0">
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="droppable2" >
                            {(provided: any, snapshot: any) => (
                                <div
                                    ref={provided.innerRef}
                                    style={getListStyle(snapshot.isDraggingOver)}>
                                    {items?.map((item, index) => (
                                        <Draggable
                                            key={item.id}
                                            draggableId={item.id.toString()}
                                            index={index}>
                                            {(provided: any, snapshot: any) => (
                                                <div>
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...(item.disabled ? {} : provided.dragHandleProps)}
                                                        style={{
                                                            ...getItemStyle(
                                                                snapshot.isDragging,
                                                                provided.draggableProps.style
                                                            ),
                                                            cursor: item.disabled ? "not-allowed" : "grab",
                                                        }}
                                                        className="d-flex align-items-center"
                                                    >
                                                        <div className="d-flex align-items-center justify-content-between w-100">
                                                            <div className="d-flex align-items-center">
                                                                <div className="form-check form-switch mb-0 mt-0" key={index}>
                                                                    <Input className="form-check-input"
                                                                        type="checkbox"
                                                                        role="switch"
                                                                        id={`switch_${index}`}
                                                                        name={`switch_${index}`}
                                                                        checked={!!item?.value}
                                                                        disabled={item.disabled}
                                                                        onChange={(event: any) => changeValueWithId(item.id, Number(event?.target.checked))}
                                                                    />
                                                                    <Label className="form-check-label" for={`switch_${index}`}>{t(`${item?.text}`)}</Label>
                                                                </div>
                                                            </div>
                                                            <div className="ms-2" style={{
                                                                opacity: item.disabled ? 0.65 : 1,
                                                            }}>
                                                                <div style={menuStyle} />
                                                                <div style={menuStyle} />
                                                                <div style={menuStyle} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </ModalBody>
                <ModalFooter>
                    <Col sm={12} className="w-100 d-flex justify-content-between">
                        <div className="gap-2 mt-3 justify-content-end ms-2">
                            <button className="btn btn-soft-secondary fs-14" type="button" color="light" onClick={() => handleSubmit(true)} disabled={isLoading}>
                                <i className="ri-refresh-line align-bottom me-1"></i>
                                {t('Button Reset Config')}
                            </button>
                        </div>
                        <div className="d-flex gap-2 mt-3 justify-content-end">
                            <button type="button"
                                className="btn btn-primary fs-14"
                                disabled={isLoading}
                                onClick={() => handleSubmit()}
                                style={{ width: '110px' }}
                            >
                                {isLoading && <Spinner size="sm me-2" ></Spinner>}
                                {t('Button Update')}
                            </button>
                            <button className="btn btn-soft-secondary fs-14" type="button" color="light" onClick={() => onCloseClick && onCloseClick()} disabled={isLoading}>
                                <i className="ri-indeterminate-circle-line align-bottom me-1"></i>
                                {t('Button Close')}
                            </button>
                        </div>
                    </Col>
                </ModalFooter>
            </Modal >
        </>
    );
};

export default ConfigDisplayInventory;